import { useNode, useEditor, ROOT_NODE } from "@craftjs/core";
import { getRandomId as getRandomNodeId } from "@craftjs/utils";
import React, { useContext, useEffect } from "react";
import { RichTextInput } from "../../input";
import { TextSettings } from "./settings";
import GrigoraCSSRenderer from "./../css-render";
import { pasteHtmlAtCaret } from "../../helpers/richtext/caret-position";
import { GrigoraDashboardContext } from "../../dashboard/context";
import {
  emitOnInput,
  getCaretPosition,
  restoreCaretPosition,
  strippedData,
  waitForElement,
  waitForLastElement,
} from "../../dom";
import { CMSTextSettings } from "./cms-settings";
import icon from "./icon";
import CMSIcon from "./cms-icon";
import { addNodeTree } from "../../editor/component-blocks";
import { blocksLibrary } from "..";
import { setEndOfContenteditable } from "../../helpers/richtext/caret-position";
import classnames from "classnames";
import { getDynamicCSS, getSupportProps } from "../../block-supports";
import { IS_MAC } from "../../utils";
import { GrigoraPreviewContext } from "../../post-preview/context";
import { toast } from "react-toastify";
import Toast from "../../toast/toast";
import { CorrectIcon } from "../../../assets/ui-icons";
import { remove_prefix } from "../../helpers";

export const Text = (props) => {
  const previewContext = useContext(GrigoraPreviewContext);
  const { cmsPosts } = useContext(GrigoraDashboardContext);
  const {
    id,
    connectors: { connect, drag },
    selected,
    dom,
    actions: { setProp },
  } = useNode((node) => ({
    id: node.id,
    selected: node.events.selected,
    dragged: node.events.dragged,
    dom: node.dom,
    state: node,
  }));

  const { query, actions } = useEditor();

  const {
    editorDocument = document,
    screen,
    blockState,
    setBlockState,
    directoryTypes,
  } = useContext(GrigoraDashboardContext) ?? {};

  const {
    query: { node, parseFreshNode },
    actions: {
      add,
      delete: deleteCraftJSNode,
      selectNode,
      setProp: setPropGlobal,
    },
  } = useEditor();

  var {
    isContentDynamic,
    dynamicContentSrc,
    dynamicContentSrcData,
    content,
    typoSize,
    layoutMargin,
    structureTag = "p",
    convertedFrom,
    hasMaxWordsLimit,
    maxTextLength = 55,
    textSuffix = "",
    suffixOnMaxWordsReached,
  } = props;

  var blockProps = getSupportProps({
    id,
    ...props,
  });

  const wrapperClass = classnames(
    {
      "g-text": true,
      [`text-${id}`]: id,
    },
    blockProps.className,
  );

  const parents = node(id).ancestors();

  useEffect(() => {
    // Convert to list if user has typed 1. or 1)
    if (structureTag === "p" && content && convertedFrom !== "list") {
      var contentDiv = editorDocument.createElement("div");
      contentDiv.innerHTML = content;
      var contentStripped = contentDiv.innerText;
      // Trim left content to remove spaces
      contentStripped = contentStripped?.trimStart();

      // Check if content stripped starts with 1. or 1) with space after it
      if (/^1\.\s/.test(contentStripped) || /^1\)\s/.test(contentStripped)) {
        var nodeID = addBlock(
          "grigora/list",
          {
            listType: "ol",
            content: [""],
            convertedFrom: "text",
            convertedText: content,
          },
          false,
        );
        deleteCraftJSNode(id);
        waitForElement(
          editorDocument,
          ".list-" + nodeID + " p.grigora-richtext-input__field",
        ).then((elm) => {
          elm.focus();
        });
      }
    }
  }, [content]);

  function addBlockInCraftJS(
    Component,
    blockProps = {},
    inPlace = false,
    canvas = false,
    focusCaretOnEnd = false,
    offset = 0,
    historyMergeAction,
    isLastBlock,
  ) {
    const parents = node(id).ancestors();
    const nodeID = getRandomNodeId();
    const freshNode = {
      id: nodeID,
      data: {
        type: Component,
        isCanvas: canvas ? true : undefined,
        props: blockProps,
      },
    };
    const nodeToAdd = parseFreshNode(freshNode).toNode();
    var node_parent = undefined;
    var node_index = undefined;
    if (parents && parents.length > 0) {
      node_parent = parents[0];
    }
    if (node_parent) {
      let parentChildrens = node(node_parent).childNodes();
      node_index = parentChildrens.indexOf(id) + offset;
    }
    (historyMergeAction ?? actions).add(nodeToAdd, node_parent, node_index + 1);
    if (inPlace) {
      (historyMergeAction ?? actions).delete(id);
    }
    waitForLastElement(
      editorDocument,
      `[data-grigora-block-id="${nodeID}"]`,
    ).then((elm) => {
      selectNode(nodeID);
    });
    if (focusCaretOnEnd && isLastBlock) {
      waitForLastElement(
        editorDocument,
        `[data-grigora-block-id="${nodeID}"] .grigora-richtext-input__field`,
      ).then((elm) => {
        selectNode(nodeID);
        setEndOfContenteditable(editorDocument, elm);
      });
    }
    return nodeID;
  }

  function addNodeSingleTree(tree, historyMergeAction) {
    var node_parent = undefined;
    var node_index = undefined;

    if (parents && parents.length > 0) {
      node_parent = parents[0];
    }
    if (node_parent) {
      let parentChildrens = node(node_parent).childNodes();
      node_index = parentChildrens.indexOf(id);
    }

    // Add new cloned node tree at current index
    if (node_parent !== undefined && node_index !== undefined) {
      addNodeTree(
        tree,
        historyMergeAction ? historyMergeAction : actions,
        query,
        node_parent,
        node_index,
      );
    }
    return;
  }

  function addBlock(
    blockSlug,
    blockProps,
    inPlace,
    focusCaretOnEnd = false,
    offset = 0,
    historyMergeAction,
    isLastBlock = false,
  ) {
    if (blocksLibrary[blockSlug]) {
      return addBlockInCraftJS(
        blocksLibrary[blockSlug],
        blockProps,
        inPlace,
        blocksLibrary[blockSlug].craft.related.supportNestedBlocks,
        focusCaretOnEnd,
        offset,
        historyMergeAction,
        isLastBlock,
      );
    }

    if (blockSlug === "grigora/heading") {
      setProp((props) => {
        props.structureTag = "h2";
        props.content = "";
      });
    }
  }

  function onSuggestionCreateBlock(blockSlug, blockProps, inPlace) {
    let historyMergeAction = actions.history.merge();

    var nodeID = addBlock(
      blockSlug,
      blockProps,
      inPlace,
      false,
      0,
      historyMergeAction,
    );
    if (nodeID) {
      waitForElement(
        editorDocument,
        `[data-grigora-block-id="` +
          nodeID +
          `"] .grigora-richtext-input__field`,
      ).then((elm) => {
        elm.focus();
      });
    }
    if (blockSlug !== "grigora/heading") {
      historyMergeAction.delete(id);
    }
    if (blockSlug === "grigora/heading") {
      waitForElement(
        editorDocument,
        ".text-" + id + " h2.grigora-richtext-input__field",
      ).then((elm) => {
        elm.focus();
      });
    }
  }

  const updateStructureTag = (newStructureTag) => {
    var caretPos = getCaretPosition(
      editorDocument,
      dom.querySelector(".grigora-richtext-input__field"),
    );
    setProp((props) => {
      props.structureTag = newStructureTag;
      props.typoSize = undefined;
    });

    waitForElement(
      editorDocument,
      ".text-" + id + ` ${newStructureTag}.grigora-richtext-input__field`,
    ).then((elm) => {
      restoreCaretPosition(editorDocument, elm, caretPos);
    });
  };

  const handleOnKeydown = (e) => {
    const ctrlKey = IS_MAC ? e.metaKey : e.ctrlKey;

    if (ctrlKey) {
      // ctrl + shift
      if (e.shiftKey) {
        switch (e.keyCode) {
          // for left align
          case 76: {
            setProp((props) => {
              props.align = "left";
            });
            e.preventDefault();
            break;
          }
          // for center align
          case 69: {
            setProp((props) => {
              props.align = "center";
            });
            e.preventDefault();
            break;
          }
          // for align right
          case 82: {
            setProp((props) => {
              props.align = "right";
            });
            e.preventDefault();
            break;
          }
          // for align justify
          case 74: {
            setProp((props) => {
              props.align = "justify";
            });
            e.preventDefault();
            break;
          }
          // for Numbered list
          case 55: {
            //create a new list block
            //update ol or ul props
            //delete this text block
            //focus on list block

            var newNode = addBlock(
              "grigora/list",
              {
                listType: "ol",
                content: [content],
              },
              false,
            );
            deleteCraftJSNode(id);
            waitForElement(
              editorDocument,
              ".list-" + newNode + " p.grigora-richtext-input__field",
            ).then((elm) => {
              elm.focus();
            });

            e.preventDefault();
            break;
          }
          // for  bulleted list
          case 56: {
            var newNode = addBlock(
              "grigora/list",
              {
                listType: "ul",
                content: [content],
              },
              false,
            );
            deleteCraftJSNode(id);
            waitForElement(
              editorDocument,
              ".list-" + newNode + " p.grigora-richtext-input__field",
            ).then((elm) => {
              elm.focus();
            });

            e.preventDefault();
            break;
          }
          // for decreasing fontsize
          case 188: {
            let currentSize = Number(
              (typoSize ? typoSize : "16").match(/(\d+)/)[0],
            );
            currentSize = currentSize ? currentSize : 16;
            if (currentSize > 8) {
              setProp((props) => {
                props.typoSize = `${currentSize - 4}px`;
              });
            }
            e.preventDefault();
            break;
          }
          // for increasing fontsize
          case 190: {
            let currentSize = Number(
              (typoSize ? typoSize : "16").match(/(\d+)/)[0],
            );
            currentSize = currentSize ? currentSize : 16;
            setProp((props) => {
              props.typoSize = `${currentSize + 4}px`;
            });
            e.preventDefault();
            break;
          }
          default: {
            break;
          }
        }
      }
      // ctrl + alt
      else if (e.altKey) {
        switch (e.keyCode) {
          // for heading 1 / H1
          case 49: {
            if (structureTag !== "h1") {
              updateStructureTag("h1");
            } else {
              updateStructureTag("p");
            }
            e.preventDefault();
            break;
          }
          // for heading 2 / H2
          case 50: {
            if (structureTag !== "h2") {
              updateStructureTag("h2");
            } else {
              updateStructureTag("p");
            }
            e.preventDefault();
            break;
          }
          // for heading 3 / H3
          case 51: {
            if (structureTag !== "h3") {
              updateStructureTag("h3");
            } else {
              updateStructureTag("p");
            }
            e.preventDefault();
            break;
          }
          // for heading 4 / H4
          case 52: {
            if (structureTag !== "h4") {
              updateStructureTag("h4");
            } else {
              updateStructureTag("p");
            }
            e.preventDefault();
            break;
          }
          // for heading 5 / H5
          case 53: {
            if (structureTag !== "h5") {
              updateStructureTag("h5");
            } else {
              updateStructureTag("p");
            }
            e.preventDefault();
            break;
          }
          // for heading 6 / H6
          case 54: {
            if (structureTag !== "h6") {
              updateStructureTag("h6");
            } else {
              updateStructureTag("p");
            }
            e.preventDefault();
            break;
          }
          default: {
            break;
          }
        }
      } else {
        switch (e.keyCode) {
          // remove paragraph indentation
          case 219: {
            if (layoutMargin === undefined) {
              setProp((props) => {
                props.layoutMargin = {
                  left: "0px",
                  top: "0px",
                  bottom: "0px",
                  right: "0px",
                };
              });
            } else {
              let currentValue = Number(layoutMargin.left.match(/(\d+)/)[0]);
              if (currentValue >= 40) {
                setProp((props) => {
                  props.layoutMargin.left = `${currentValue - 40}px`;
                });
              } else {
                setProp((props) => {
                  props.layoutMargin.left = "0px";
                });
              }
            }
            e.preventDefault();
            break;
          }
          // add paragraph indentation
          case 221: {
            if (layoutMargin === undefined) {
              setProp((props) => {
                props.layoutMargin = {
                  left: "40px",
                  top: "0px",
                  bottom: "0px",
                  right: "0px",
                };
              });
            } else {
              let currentValue = Number(layoutMargin.left.match(/(\d+)/)[0]);
              setProp((props) => {
                props.layoutMargin.left = `${currentValue + 40}px`;
              });
            }
            e.preventDefault();
            break;
          }
          default: {
            break;
          }
        }
      }
    }
  };

  var Tag = structureTag;

  function getDynamicText() {
    var dynamicText;
    switch (dynamicContentSrc) {
      case "post-title":
        var title = previewContext?.postData?.title
          ? previewContext?.postData?.title
          : cmsPosts.length > 0
          ? cmsPosts?.[0]?.title
          : "Post Title";
        dynamicText = title ? title : "";
        break;
      case "post-excerpt":
        var summary = previewContext?.postData?.title
          ? previewContext?.postData?.title
          : cmsPosts?.length > 0
          ? cmsPosts?.[0]?.summary
          : "Post Excerpt";
        dynamicText = summary ? summary : "";
        break;
      case "category-name":
        var name = previewContext?.postData?.category
          ? previewContext?.postData?.category
          : cmsPosts?.length > 0 && cmsPosts?.[0]?.category?.length > 0
          ? cmsPosts?.[0]?.category
              ?.map((category) => category?.label)
              .join(", ")
          : "Post Category";
        dynamicText = name ? name : "";
        break;
      case "category-description":
        var description = "Category Description";
        dynamicText = description ? description : "";
        break;
      default:
        var directory = directoryTypes?.find(
          (type) =>
            remove_prefix(type.sk, "directory-type-") ===
            dynamicContentSrcData?.directory_type_id,
        );
        var fieldLabel = directory?.fields?.find(
          (field) => field.id === dynamicContentSrcData?.directory_field,
        )?.label;
        if (directory && fieldLabel) {
          dynamicText = `${directory?.name} - ${fieldLabel}`;
        } else {
          dynamicText = "Please select a dynamic source";
        }
        break;
    }
    if (!dynamicText) {
      return "";
    }

    let trimmed = dynamicText.trim();

    // trim
    let textLength = trimmed.split(" ").length;
    if (hasMaxWordsLimit && maxTextLength) {
      trimmed = trimmed.split(" ").splice(0, maxTextLength).join(" ");
    }

    // suffix
    if (
      textSuffix &&
      (!suffixOnMaxWordsReached ||
        (hasMaxWordsLimit &&
          suffixOnMaxWordsReached &&
          textLength > maxTextLength))
    ) {
      if (trimmed.toLowerCase().endsWith(textSuffix.toLowerCase())) {
        trimmed = trimmed.slice(0, -textSuffix.length).concat(textSuffix);
      }
      if (!trimmed.toLowerCase().endsWith(textSuffix.toLowerCase())) {
        trimmed = trimmed.concat(textSuffix);
      }
    }
    return trimmed;
  }

  const renderDynamicContent = () => {
    return <Tag>{getDynamicText()}</Tag>;
  };

  return (
    <div
      {...blockProps}
      {...(screen === "cms-post-editor"
        ? { ref: (ref) => connect(ref) }
        : { ref: (ref) => connect(drag(ref)) })}
      className={wrapperClass}
      onKeyDown={handleOnKeydown}
      onFocus={() => {
        selectNode(id);
      }}
      onClick={(e) => {
        // If double clicked
        if (isContentDynamic) {
          if (e.detail === 2) {
            toast.info(
              <Toast
                title={"Text is non editable"}
                description={
                  "Please check the dynamic source from block settings"
                }
              />,
              {
                // icon: <CorrectIcon />,
              },
            );
          }
        }
      }}
      // onDragOver={(e)=>{
      // console.log("drag over text");
      // log target
      // console.log(e.target);
      // }}
      data-grigora-block="true"
      data-grigora-block-id={id}
      data-grigora-block-type="grigora/text"
      data-is-empty={content ? "false" : "true"}
      data-structure-tag={structureTag}
    >
      <style>{`${getDynamicCSS(id, props)}`}</style>
      <style>{`${GrigoraCSSRenderer.blockCSS__Text(id, props)}`}</style>
      {isContentDynamic ? (
        <>{renderDynamicContent()}</>
      ) : (
        <>
          <RichTextInput
            selected={selected}
            value={content}
            allowedSuggestion={true}
            onChange={(e, historyMergeAction) => {
              if (historyMergeAction) {
                historyMergeAction.setProp(
                  id,
                  (props) => (props.content = e),
                  500,
                );
                return;
              }
              setProp((props) => (props.content = e), 500);
            }}
            tagName={structureTag}
            placeholder={
              structureTag === "h1"
                ? "Heading 1"
                : structureTag === "h2"
                ? "Heading 2"
                : structureTag === "h3"
                ? "Heading 3"
                : structureTag === "h4"
                ? "Heading 4"
                : structureTag === "h5"
                ? "Heading 5"
                : structureTag === "h6"
                ? "Heading 6"
                : "Press / for elements"
            }
            // disabled={!editable}
            onSplit={(newValue, oldValue, historyMergeAction) => {
              const nodeID = getRandomNodeId();
              const freshNode = {
                id: nodeID,
                data: {
                  type: Text,
                  props: {
                    ...(newValue.trim() ? props : {}),
                    content: newValue.trim(),
                  },
                },
              };
              const nodeToAdd = parseFreshNode(freshNode).toNode();
              var node_parent = undefined;
              var node_index = undefined;
              if (parents && parents.length > 0) {
                node_parent = parents[0];
              }
              if (node_parent) {
                let parentChildrens = node(node_parent).childNodes();
                node_index = parentChildrens.indexOf(id) + 1;
              }

              if (historyMergeAction) {
                historyMergeAction.add(nodeToAdd, node_parent, node_index);
              } else {
                add(nodeToAdd, node_parent, node_index);
              }

              waitForElement(
                editorDocument,
                ".text-" + nodeID + " .grigora-richtext-input__field",
              ).then((elm) => {
                selectNode(nodeID);
                elm.focus();
              });
            }}
            onMerge={(forward) => {
              const parents = node(id).ancestors();

              let historyMergeAction = actions.history.merge();

              // Merge with next Block
              if (forward) {
                if (parents && parents.length > 0) {
                  let parentChildrens = node(parents[0]).childNodes();
                  let node_index = parentChildrens.indexOf(id) + 1;
                  if (parentChildrens[node_index]) {
                    var nodeData = node(parentChildrens[node_index])?.get()
                      .data;

                    // Merge with next text block
                    if (nodeData && nodeData?.custom?.slug === "grigora/text") {
                      var forward_node = nodeData?.props?.content;
                      if (forward_node) {
                        historyMergeAction.setProp(id, (props) => {
                          props.content += forward_node;
                        });
                      }
                      historyMergeAction.delete(parentChildrens[node_index]);
                    }

                    // Merge with next list block
                    if (nodeData && nodeData?.custom?.slug === "grigora/list") {
                      var forward_node = nodeData?.props?.content ?? [""];
                      historyMergeAction.setProp(id, (props) => {
                        props.content += forward_node[0];
                      });

                      if (forward_node.length > 1) {
                        historyMergeAction.setProp(
                          parentChildrens[node_index],
                          (props) => {
                            props.content = forward_node.slice(1);
                          },
                        );
                      } else {
                        historyMergeAction.delete(parentChildrens[node_index]);
                      }
                    }
                  }
                }
              }
              // Merge with previous Block
              else {
                if (parents && parents.length > 0) {
                  let parentChildrens = node(parents[0]).childNodes();
                  let node_index = parentChildrens.indexOf(id) - 1;

                  // If parent is root node and current node is first node
                  if (parents[0] === ROOT_NODE && node_index === -1) {
                    if (!strippedData(content)) {
                      // Focus on end of post title
                      waitForElement(
                        editorDocument,
                        `.grigora-post-editor-post-title .grigora-richtext-input__field`,
                      ).then((elm) => {
                        setEndOfContenteditable(editorDocument, elm);
                      });
                      historyMergeAction.delete(id);
                    }
                    return;
                  }

                  if (node_index > -1 && parentChildrens[node_index]) {
                    var nodeData = node(parentChildrens[node_index])?.get()
                      ?.data;
                    // If previous block is text
                    if (nodeData && nodeData?.custom?.slug === "grigora/text") {
                      var prev_node = nodeData?.props?.content;

                      setEndOfContenteditable(
                        editorDocument,
                        editorDocument.querySelector(
                          `[data-grigora-block-id='${parentChildrens[node_index]}'] .grigora-richtext-input__field`,
                        ),
                      );

                      pasteHtmlAtCaret(editorDocument, content ?? "", true);

                      historyMergeAction.setProp(
                        parentChildrens[node_index],
                        (props) => {
                          props.content += content ?? "";
                        },
                      );

                      historyMergeAction.delete(id);

                      // if (prev_node) {
                      //   // Check if stripped data is empty
                      //   var prev_nodeDiv = editorDocument.createElement("div");
                      //   prev_nodeDiv.innerHTML = prev_node ?? "";
                      //   var prev_nodeStripped = prev_nodeDiv.innerText;

                      //     pasteHtmlAtCaret(editorDocument, prev_node);
                      //     historyMergeAction.setProp(id, (props) => {
                      //       Object.keys(nodeData?.props).map((key) => {
                      //         if (["content", "children"].includes(key)) {
                      //           return;
                      //         }
                      //         props[key] = nodeData?.props
                      //           ? nodeData?.props[key]
                      //           : undefined;
                      //       });
                      //       if(prev_nodeStripped){
                      //         props.content = prev_node + (props.content ?? "");
                      //       }
                      //     });
                      // } else {
                      //   // Delete self and focus on previous
                      //   node_index = node_index + 1;

                      //   waitForLastElement(
                      //     editorDocument,
                      //     `[data-grigora-block-id='${parentChildrens[node_index - 1]}'] .grigora-richtext-input__field`,
                      //   ).then((elm) => {
                      //     setEndOfContenteditable(editorDocument, elm);
                      //   });
                      // }
                      // historyMergeAction.delete(parentChildrens[node_index]);
                    }
                    // If previous block is a list
                    else if (
                      nodeData &&
                      nodeData?.custom?.slug === "grigora/list"
                    ) {
                      var prev_node = nodeData?.props?.content;
                      if (prev_node) {
                        historyMergeAction.setProp(
                          parentChildrens[node_index],
                          (props) => {
                            const temp = [...props.content];
                            temp.push(content);
                            props.content = temp;
                            return props;
                          },
                        );
                      }
                      historyMergeAction.delete(id);
                      waitForElement(
                        editorDocument,
                        `.list-${parentChildrens[node_index]} .c .i-${prev_node.length} .grigora-richtext-input .grigora-richtext-input__field`,
                      ).then((elm) => {
                        elm.focus();
                      });
                    }
                    // If current block is empty and previous block is not mergable
                    // And previous block should exists
                    else if (!strippedData(content)) {
                      historyMergeAction.delete(id);
                      waitForLastElement(
                        editorDocument,
                        `[data-grigora-block-id='${parentChildrens[node_index]}'] .grigora-richtext-input__field`,
                      ).then((elm) => {
                        elm.focus();
                      });
                    }
                  }
                }
              }
            }}
            onSuggestionCreateBlock={onSuggestionCreateBlock}
            onPasteParse={(
              blocks,
              nodeTreesFormat = false,
              forceDeleteSelected = false,
            ) => {
              var selectedBlocks = query.getEvent("selected").all() ?? [];
              var deleteSelf = false;
              if (content === "") {
                deleteSelf = true;
              }
              if (
                selectedBlocks.length === 1 &&
                blocks.length === 1 &&
                blocks[0].blockSlug === "grigora/text"
              ) {
                pasteHtmlAtCaret(editorDocument, blocks[0].blockProps.content);
                emitOnInput(
                  dom.querySelector(".grigora-richtext-input__field"),
                );
                return;
              }

              let historyMergeAction = actions.history.merge();

              for (let i = 0; i < blocks.length; i++) {
                const block = blocks[i];

                if (nodeTreesFormat) {
                  addNodeSingleTree(block, historyMergeAction);
                } else {
                  addBlock(
                    block.blockSlug,
                    block.blockProps,
                    false,
                    true,
                    i,
                    historyMergeAction,
                    i === blocks.length - 1,
                  );
                }
              }
              if (deleteSelf) {
                selectedBlocks.forEach((id) => {
                  historyMergeAction.delete(id);
                });
              }
              if (nodeTreesFormat && deleteSelf) {
                selectedBlocks.forEach((id) => {
                  historyMergeAction.delete(id);
                });
              }

              if (forceDeleteSelected) {
                selectedBlocks.forEach((id) => {
                  historyMergeAction.delete(id);
                });
              }

              // Focus on newly added block
            }}
          />
        </>
      )}
    </div>
  );
};

// To Avoid Mangling in Production
Text.displayName = "Text";

Text.craft = {
  name: "Text",
  props: {},
  related: {
    settings: TextSettings,
    CMSsettings: CMSTextSettings,
    icon: icon,
    CMSIcon: CMSIcon,
    supports: [
      "grigora/directory-conditional",
      "grigora/entrance-animations",
      "grigora/css-transform-animations",
      "grigora/mouse-movement",
      "grigora/visibility",
      "grigora/position",
      "grigora/sticky",
      "grigora/filter",
      "grigora/custom-class",
      "grigora/custom-id",
    ],
    category: "basic",
  },
  custom: {
    name: "Text",
    slug: "grigora/text",
  },
};
